import { forwardRef, type SelectHTMLAttributes, type CSSProperties } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'style'> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  style?: CSSProperties;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, helperText, options, style, ...rest },
  ref
) {
  const errBorder = 'oklch(0.55 0.18 25)';
  return (
    <div style={{ width: '100%' }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: 12.5,
            fontWeight: 500,
            color: 'var(--fg-muted, oklch(0.56 0.006 230))',
            marginBottom: 6,
            letterSpacing: '-0.005em',
          }}
        >
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <select
          ref={ref}
          style={{
            display: 'block',
            width: '100%',
            height: 34,
            padding: '0 32px 0 12px',
            fontSize: 13.5,
            letterSpacing: '-0.005em',
            color: 'var(--fg, oklch(0.94 0.003 230))',
            background: 'var(--surface-2, oklch(0.14 0.012 230))',
            border: `1px solid ${error ? errBorder : 'var(--border, oklch(0.18 0.012 230))'}`,
            borderRadius: 'var(--r-2, 6px)',
            outline: 'none',
            appearance: 'none',
            transition: 'border-color 120ms ease, box-shadow 120ms ease',
            ...style,
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = error ? errBorder : 'var(--ice-500, oklch(0.66 0.150 205))';
            e.currentTarget.style.boxShadow = '0 0 0 2px oklch(0.66 0.150 205 / 0.25)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = error ? errBorder : 'var(--border, oklch(0.18 0.012 230))';
            e.currentTarget.style.boxShadow = 'none';
          }}
          {...rest}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'var(--fg-dim, oklch(0.40 0.008 230))',
          }}
        >
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {error && (
        <p style={{ marginTop: 6, fontSize: 12, color: 'oklch(0.80 0.12 25)' }}>{error}</p>
      )}
      {helperText && !error && (
        <p style={{ marginTop: 6, fontSize: 12, color: 'var(--fg-dim, oklch(0.40 0.008 230))' }}>{helperText}</p>
      )}
    </div>
  );
});

export default Select;
